import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { useT } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';
import { ancora, daAncora, procurar, type Ajuda as AjudaManual, type AjudaNo } from '@/lib/ajuda';

// A ajuda do painel. O manual chega da API JÁ CORTADO pelo papel da sessão e pelo
// trilho do projeto: aqui não existe filtro de permissão, e não deve existir — o que
// esta pessoa não pode ler nunca saiu do servidor.
export function Ajuda() {
  const t = useT();
  // O "?" de uma tela sem capítulo escrito chega aqui com o nome dela em `q`: a busca
  // já vem preenchida, em vez de despejar a pessoa num manual inteiro.
  const [params] = useSearchParams();
  const [termo, setTermo] = useState(() => params.get('q') ?? '');
  const [alvo, setAlvo] = useState(() => daAncora(window.location.hash));
  const corpo = useRef<HTMLDivElement>(null);

  const { data: manual, isLoading } = useQuery<AjudaManual>({
    queryKey: ['ajuda'],
    queryFn: async () => (await apiFetch('GET', '/help')).data as AjudaManual,
    staleTime: 10 * 60 * 1000, // o manual muda a cada deploy, não a cada navegação
  });

  // Link copiado abre na seção: a âncora manda tanto na entrada quanto no botão
  // "voltar" do navegador.
  useEffect(() => {
    const ouvir = () => setAlvo(daAncora(window.location.hash));
    window.addEventListener('hashchange', ouvir);
    return () => window.removeEventListener('hashchange', ouvir);
  }, []);

  useEffect(() => {
    if (!alvo || !manual) return;
    const el = corpo.current?.querySelector(`[data-ancora="${ancora(alvo.capitulo, alvo.secao)}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [alvo, manual]);

  const achadas = useMemo(
    () => (termo.trim() && manual ? procurar(manual.busca, termo) : null),
    [termo, manual],
  );

  const capitulos = useMemo(() => {
    if (!manual) return [];
    if (!achadas) return manual.capitulos;
    return manual.capitulos
      .map((c) => ({
        ...c,
        secoes: c.secoes.filter((s) =>
          achadas.has(`${c.id}#${s.id}`) || (s.subsecoes ?? []).some((x) => achadas.has(`${c.id}#${x.id}`))),
      }))
      .filter((c) => c.secoes.length > 0);
  }, [manual, achadas]);

  const irPara = (capituloId: string, secaoId: string) => {
    window.location.hash = ancora(capituloId, secaoId);
  };

  if (isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;

  if (!manual?.capitulos.length) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-2 py-16 text-center">
          <BookOpen className="size-8 text-primary" />
          <p className="text-sm text-muted-foreground">{t('ajuda.vazia')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={termo}
          onChange={(e) => setTermo(e.target.value)}
          placeholder={t('ajuda.buscar')}
          className="pl-9"
          aria-label={t('ajuda.buscar')}
        />
      </div>

      {achadas && capitulos.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('ajuda.semResultado')}</p>
      )}

      <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
        {/* Índice: no desktop fica fixo ao lado; no celular vira a lista do topo. */}
        <nav className="hidden flex-col gap-4 lg:flex">
          {capitulos.map((c) => (
            <div key={c.id} className="flex flex-col gap-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{c.titulo}</p>
              {c.secoes.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => irPara(c.id, s.id)}
                  className={cn('rounded px-2 py-1 text-left text-sm transition-colors hover:bg-muted',
                    alvo?.secao === s.id && 'bg-muted font-medium')}
                >
                  {s.numero} {s.titulo}
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div ref={corpo} className="ajuda-conteudo flex min-w-0 flex-col gap-8">
          {capitulos.map((c) => (
            <section key={c.id} data-ancora={ancora(c.id)} className="flex scroll-mt-20 flex-col gap-6">
              <div>
                <h2 className="text-lg font-semibold">{c.numero}. {c.titulo}</h2>
                {c.html && <div className="mt-2" dangerouslySetInnerHTML={{ __html: c.html }} />}
              </div>
              {c.secoes.map((s) => (
                <Secao key={s.id} capituloId={c.id} no={s} nivel={3} aoCopiar={irPara} />
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}

function Secao({ capituloId, no, nivel, aoCopiar }: {
  capituloId: string; no: AjudaNo; nivel: number; aoCopiar: (c: string, s: string) => void;
}) {
  const Titulo = (nivel === 3 ? 'h3' : 'h4') as 'h3' | 'h4';
  return (
    <article data-ancora={ancora(capituloId, no.id)} className="scroll-mt-20">
      <Titulo className={cn('font-semibold', nivel === 3 ? 'text-base' : 'text-sm')}>
        <button type="button" onClick={() => aoCopiar(capituloId, no.id)} className="hover:underline">
          {no.numero} {no.titulo}
        </button>
      </Titulo>
      {/* O HTML vem do nosso gerador, a partir da nossa fonte versionada: não há
          entrada de usuário em lugar nenhum deste caminho. */}
      <div className="mt-2" dangerouslySetInnerHTML={{ __html: no.html }} />
      {(no.subsecoes ?? []).map((sub) => (
        <div key={sub.id} className="mt-5 border-l-2 border-border pl-4">
          <Secao capituloId={capituloId} no={sub} nivel={4} aoCopiar={aoCopiar} />
        </div>
      ))}
    </article>
  );
}
